import { useEffect } from "react";
import { TopBar } from "./kit";
import LogStepEyebrow from "./LogStepEyebrow";
import LogMatchSavedStep from "./LogMatchSavedStep";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useRetireTextSize } from "../context/RetireTextSizeContext";
// Issue #759, brief-759.md ruling ("also add the contrast trigger
// `topbar-accessibility`, as on steps 1 and 2 — D26 (decisions-2026-09-24.md:190)
// says every navy-TopBar artboard in waves 2–6 carries the contrast and
// account menus, and the Saved drawing leaves it out") — the SAME three
// shared controls `LogGroundFlow.jsx`/`LogScorecardFlow.jsx` already reuse
// verbatim, not forked.
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";

/**
 * The step's own page surface (mirrors `LogGroundFlow.jsx`/
 * `LogScorecardFlow.jsx`). Mounted only while `PerformanceTracking.jsx`'s
 * own `showLogSavedStep` is true.
 *
 * No `mode` prop, unlike `LogScorecardFlow` — step 3 is reached only from a
 * fresh create (never an edit: see `PerformanceTracking.jsx`'s own
 * `create()`), so there is exactly one shape of this step's `TopBar`, always
 * carrying the step counter.
 */
export default function LogSavedFlow(props) {
  const { setActive: setChromeless } = useChromelessOverride();
  const { setRetired: setTextSizeRetired } = useRetireTextSize();
  useEffect(() => {
    setChromeless(true);
    setTextSizeRetired(true);
    // PR #844 review, B2 — step 2 (the scorecard) can leave the page
    // scrolled well down (a long form, or the player scrolling to reach
    // "Put it on the record"); React swaps that whole tree out for this
    // one on save, but the BROWSER's own scroll position is not a React
    // concern and carries straight over. Left alone, step 3 arrived with
    // scrollY around 231px — the title and progress pills sat above the
    // fold, and (before the toast was also dropped, see
    // `PerformanceTracking.jsx`'s `create()`) the "Match logged!" toast
    // rendered over the Batting tiles instead of the top of the page it
    // was meant to sit above. Reset once, on arrival.
    window.scrollTo(0, 0);
    return () => {
      setChromeless(false);
      setTextSizeRetired(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    // Round-5 token-check, same reasoning as the other two steps' own root —
    // an explicit `color` so nothing under this mount inherits the app
    // shell's off-palette default.
    <div data-testid="log-saved-flow" style={{ color: "var(--nw-ink)" }}>
      <TopBar
        variant="navy"
        // PR #887 review, B4 — `topbar-logo` + the home link; player-only
        // screen, home is always `/dashboard`.
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        endControls={
          <>
            <NotificationsBell compact />
            <AccessibilityMenu hideTextSize />
            {/* #780 (D140) — the drawn empty account circle; see AccountMenu.jsx's own docstring on the `empty` prop. */}
            <AccountMenu empty />
          </>
        }
        data-testid="log-step-3-topbar"
      />
      <LogStepEyebrow step={3} total={3} />
      <LogMatchSavedStep {...props} />
    </div>
  );
}
